import { useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

/**
 * Custom hook to track page visits.
 *
 * @param {Object} options
 * @param {boolean} options.autoTrack - If true (default), automatically tracks the current route
 *   on mount and on navigation. Set to false in components that only need the `trackPage` function
 *   for manual tracking (e.g. external link clicks), to avoid double-counting.
 *
 * Usage:
 *   App.js              → usePageVisitTracker()              (autoTrack = true, tracks routes)
 *   Other components    → usePageVisitTracker({ autoTrack: false })  (only use trackPage manually)
 */
const usePageVisitTracker = ({ autoTrack = true } = {}) => {
  const location = useLocation();
  const baseUrl = useSelector((state) => state.url);

  const getSessionId = useCallback(() => {
    let sessionId = sessionStorage.getItem("analytics_session_id");
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
      sessionStorage.setItem("analytics_session_id", sessionId);
    }
    return sessionId;
  }, []);

  const trackPage = useCallback(async (page) => {
    if (!baseUrl || !page) return;
    try {
      await fetch(`${baseUrl}/api/v1/analytics/visit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page, sessionId: getSessionId() }),
      });
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.debug("Analytics tracking failed:", err);
      }
    }
  }, [baseUrl, getSessionId]);

  useEffect(() => {
    if (!autoTrack) return;
    if (!baseUrl) return;

    const excludedPages = ["/analytics", "/access-management"];
    if (excludedPages.includes(location.pathname)) return;

    trackPage(location.pathname);
  }, [autoTrack, baseUrl, location.pathname, trackPage]);

  return { trackPage };
};

export default usePageVisitTracker;
